import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '../../../../support/store';
import {
  useQuizAccessInfo,
  useQuizUserAttempts,
  useQuizStartAttempt,
  useViewQuizMutation,
  useQuizUserBestGrade,
  useQuizzesByCourses,
} from '../../../../hooks/useMoodleQuiz';
import { getUserCourseRoles } from '../../../../hooks/useMoodleHooks';
import {
  setPreviousRoles,
  clearPreviousRoles,
  resetQuiz,
} from '../../../../support/slices/moodleSlice';
import StbLoader from '../../../security-toolbox-ui/StbLoader';
import { MoodleInstance } from '../../../../hooks/useMoodleHooks';

type Attempt = {
  id: number;
  quiz: number;
  userid: number;
  attempt: number;
  state?: string;
  preview?: number;
  timefinish?: number;
  sumgrades?: number;
};

interface Props {
  courseId?: string | number;
  quizId?: string | number;
  instance: MoodleInstance;
  onStartAttempt?: (attemptId: number) => void;
  onBackToCourse?: () => void;
}

const SafeLoader: React.FC<{ isLoading: boolean; message?: string }> = ({ isLoading, message }) => {
  if (!isLoading) return null;
  try {
    return (
      <div style={{ padding: 'var(--hbr-spacing-lg)', display: 'flex', justifyContent: 'center' }}>
        <StbLoader isLoading={true} />
      </div>
    );
  } catch {
    return <div>{message || 'Loading...'}</div>;
  }
};

export default function MoodleQuizEntry({
                                          courseId,
                                          quizId,
                                          instance,
                                          onStartAttempt,
                                          onBackToCourse,
                                        }: Props) {
  const dispatch = useDispatch();

  const cid = courseId != null ? Number(courseId) : undefined;
  const qid = quizId != null ? Number(quizId) : undefined;

  const moodleUserId = useSelector((state: RootState) =>
    instance === 'internal'
      ? state.moodle.instances.internal.user?.id
      : state.moodle.instances.external.user?.id
  );

  const previousRoles = useSelector((state: RootState) =>
    instance === 'internal'
      ? state.moodle.instances.internal.user.previousRoles
      : state.moodle.instances.external.user.previousRoles
  );

  const [showRoleModal, setShowRoleModal] = useState(false);
  const [rolesChecked, setRolesChecked] = useState(false);

  const {
    data: access,
    isLoading: loadingAccess,
    error: accessError,
  } = useQuizAccessInfo(qid, { enabled: !!qid }, instance);

  const {
    data: allQuizzes,
    isLoading: loadingQuizzes,
    error: quizzesError,
  } = useQuizzesByCourses(cid, { enabled: !!cid }, instance);

  const quizData = useMemo(() => allQuizzes?.find((q) => q.id === qid), [allQuizzes, qid]);

  const {
    data: rawAttemptsData,
    isLoading: loadingAttempts,
    refetch: refetchAttempts,
    error: attemptsError,
  } = useQuizUserAttempts(
    qid ?? 0,
    { userid: moodleUserId ?? 0, status: 'all', includepreviews: true },
    { enabled: !!qid },
    instance
  );

  const attempts: Attempt[] = useMemo(() => {
    if (Array.isArray(rawAttemptsData)) return rawAttemptsData;
    if (rawAttemptsData && Array.isArray((rawAttemptsData as any).attempts)) {
      return (rawAttemptsData as any).attempts;
    }
    return [];
  }, [rawAttemptsData]);

  const resolvedUserId = useMemo(() => {
    if (moodleUserId) return moodleUserId;
    if (attempts.length > 0) return attempts[0].userid;
    return undefined;
  }, [moodleUserId, attempts]);

  const { data: bestGrade } = useQuizUserBestGrade(
    qid ?? 0,
    resolvedUserId ?? 0,
    { enabled: !!qid && !!resolvedUserId },
    instance
  );

  const inProgressAttempt = useMemo(
    () => attempts.find((a) => String(a.state || '').toLowerCase() === 'inprogress'),
    [attempts]
  );

  const finishedAttempts = useMemo(
    () =>
      attempts
        .filter((a) => String(a.state || '').toLowerCase() === 'finished')
        .sort((a, b) => (b.timefinish || 0) - (a.timefinish || 0)),
    [attempts]
  );

  const quizRawMax = Number(quizData?.sumgrades ?? access?.sumgrades ?? NaN);
  const gradebookMax = Number(quizData?.grade ?? access?.grade ?? NaN);
  const gradeToPass = Number(
    (quizData as any)?.gradetopass ??
    (access as any)?.gradetopass ??
    (bestGrade as any)?.gradetopass ??
    NaN
  );

  const latestFinishedAttempt = finishedAttempts[0];
  const latestAttemptScore = latestFinishedAttempt?.sumgrades ?? inProgressAttempt?.sumgrades ?? NaN;

  const hasQuizRawMax = Number.isFinite(quizRawMax) && quizRawMax > 0;
  const hasLatestAttemptScore = Number.isFinite(Number(latestAttemptScore));

  const hasLatestScore = hasQuizRawMax && hasLatestAttemptScore;

  const latestScorePercent = hasLatestScore
    ? (Number(latestAttemptScore) / quizRawMax) * 100
    : NaN;

  const hasPassThreshold =
    Number.isFinite(gradebookMax) &&
    gradebookMax > 0 &&
    Number.isFinite(gradeToPass);

  const percentageToPass = hasPassThreshold ? gradeToPass / gradebookMax : NaN;

  const officiallyPassed =
    hasPassThreshold &&
    Number.isFinite(latestScorePercent) &&
    latestScorePercent / 100 >= percentageToPass;

  const { mutate: startAttempt, isPending: starting } = useQuizStartAttempt(
    {
      onSuccess: (res) => {
        const aid = res?.attempt?.id ?? res?.id ?? res?.attemptid;
        if (aid) {
          dispatch(resetQuiz({ instance }));
          onStartAttempt?.(aid);
        } else {
          refetchAttempts();
        }
      },
    },
    instance
  );

  const { mutate: markViewed } = useViewQuizMutation(undefined, instance);
  const hasViewedRef = useRef(false);

  useEffect(() => {
    if (qid && !hasViewedRef.current && !!resolvedUserId) {
      markViewed({ quizId: qid });
      hasViewedRef.current = true;
    }
  }, [qid, resolvedUserId, markViewed]);

  const canAttempt = !!access?.canattempt && !(access as any)?.isfinished;

  useEffect(() => {
    let cancelled = false;

    async function checkRoles() {
      if (!cid) {
        if (!cancelled) setRolesChecked(true);
        return;
      }

      if (!resolvedUserId) {
        if (!cancelled) setRolesChecked(true);
        return;
      }

      try {
        const roles = await getUserCourseRoles(cid, resolvedUserId, instance);
        if (cancelled) return;

        const nonEmployee = roles.filter((r) => r.shortname.toLowerCase() !== 'employee');
        if (nonEmployee.length > 0) {
          dispatch(setPreviousRoles({ roles, instance }));
          setShowRoleModal(true);
        }
      } catch (error) {
        console.warn('Role check failed; allowing quiz entry to continue.', error);
      } finally {
        if (!cancelled) setRolesChecked(true);
      }
    }

    setRolesChecked(false);
    checkRoles();

    return () => {
      cancelled = true;
    };
  }, [cid, resolvedUserId, dispatch, instance]);

  useEffect(() => {
    if (previousRoles?.length > 0 && finishedAttempts.length > 0) {
      dispatch(clearPreviousRoles({ instance }));
    }
  }, [finishedAttempts, previousRoles, dispatch, instance]);

  function handleContinue() {
    if (inProgressAttempt?.id) {
      onStartAttempt?.(inProgressAttempt.id);
    }
  }

  function handleNew() {
    if (showRoleModal) return;
    startAttempt({ quizId: qid ?? 0, forcenew: true });
  }

  const cancelRoleAdjust = () => setShowRoleModal(false);

  if (!qid || !cid) return <p>Invalid quiz configuration.</p>;

  if (loadingAccess || loadingQuizzes || !rolesChecked || loadingAttempts) {
    return <SafeLoader isLoading={true} message="Loading quiz information..." />;
  }

  if (accessError) {
    return <p role="alert" style={{ color: 'red' }}>{accessError.message}</p>;
  }

  if (quizzesError) {
    return <p role="alert" style={{ color: 'red' }}>{quizzesError.message}</p>;
  }

  if (attemptsError) {
    return <p role="alert" style={{ color: 'red' }}>{attemptsError.message}</p>;
  }

  return (
    <div style={{ padding: 'var(--hbr-spacing-md)', display: 'grid', gap: 'var(--hbr-spacing-lg)' }}>
      {showRoleModal && rolesChecked && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
          }}
        >
          <div
            style={{
              background: '#fff',
              padding: '2rem',
              borderRadius: '8px',
              maxWidth: '400px',
              boxShadow: '0 10px 25px rgba(0,0,0,0.2)',
            }}
          >
            <h3 style={{ margin: '0 0 1rem 0' }}>Information</h3>
            <p style={{ margin: '0 0 1.5rem 0' }}>You may proceed with the quiz.</p>
            <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
              {onBackToCourse && (
                <button
                  className="btn"
                  onClick={onBackToCourse}
                  style={{ marginRight: '0.5rem' }}
                >
                  Cancel
                </button>
              )}
              <button className="btn btn-primary" onClick={cancelRoleAdjust}>
                Ok
              </button>
            </div>
          </div>
        </div>
      )}

      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--hbr-spacing-sm)' }}>
        <h2 style={{ margin: 0 }}>{quizData?.name || `Quiz ${qid}`}</h2>
      </div>

      <div className="hbr-card">
        <div className="hbr-card-body">
          <h3>Latest Result</h3>

          {finishedAttempts.length > 0 || hasLatestScore ? (
            <>
              {hasLatestScore ? (
                <div style={{ marginBottom: '0.5rem' }}>
                  Latest Score: <strong>{latestScorePercent.toFixed(0)}%</strong>
                </div>
              ) : (
                <div style={{ marginBottom: '0.5rem' }}>
                  Latest Attempt: <strong>Completed</strong>
                </div>
              )}

              {hasPassThreshold && (
                <div style={{ marginBottom: '0.5rem' }}>
                  Grade To Pass: <strong>{(percentageToPass * 100).toFixed(0)}%</strong>
                </div>
              )}

              {hasPassThreshold ? (
                <div
                  style={{
                    padding: '0.5rem 1rem',
                    borderRadius: 6,
                    fontWeight: 600,
                    color: officiallyPassed ? '#2e7d32' : '#b23c17',
                    backgroundColor: officiallyPassed ? '#e8f5e9' : '#ffebee',
                    border: `2px solid ${officiallyPassed ? '#2e7d32' : '#b23c17'}`,
                    display: 'inline-block',
                    fontSize: '0.9rem',
                  }}
                >
                  {officiallyPassed ? '✅ Passed' : '❌ Not Passed'}
                </div>
              ) : (
                <div
                  style={{
                    padding: '0.5rem 1rem',
                    borderRadius: 6,
                    fontWeight: 600,
                    color: '#374151',
                    backgroundColor: '#f3f4f6',
                    border: '2px solid #d1d5db',
                    display: 'inline-block',
                    fontSize: '0.9rem',
                  }}
                >
                  Completed
                </div>
              )}
            </>
          ) : (
            <p>No completed score available yet.</p>
          )}
        </div>
      </div>

      <div className="hbr-card">
        <div
          className="hbr-card-body"
          style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'center' }}
        >
          {inProgressAttempt && (
            <button className="btn btn-primary" onClick={handleContinue} disabled={!canAttempt || starting}>
              {starting ? 'Starting...' : `Continue Attempt #${inProgressAttempt.attempt}`}
            </button>
          )}
          <button className="btn btn-primary" onClick={handleNew} disabled={!canAttempt || starting}>
            {starting ? 'Starting...' : 'Start New Attempt'}
          </button>
        </div>
      </div>

      <div className="hbr-card">
        <div className="hbr-card-body">
          <h3>Quiz Attempts</h3>
          {finishedAttempts.length ? (
            <div style={{ overflowX: 'auto' }}>
              <table className="hbr-table">
                <thead>
                <tr>
                  <th>Attempt</th>
                  <th>Finished</th>
                  <th>Score</th>
                  <th>Result</th>
                </tr>
                </thead>
                <tbody>
                {finishedAttempts.map((a) => {
                  const pctAchieved =
                    Number.isFinite(quizRawMax) && Number.isFinite(Number(a.sumgrades))
                      ? (Number(a.sumgrades) / quizRawMax) * 100
                      : NaN;

                  const passed =
                    hasPassThreshold &&
                    Number.isFinite(pctAchieved) &&
                    pctAchieved / 100 >= percentageToPass;

                  return (
                    <tr key={a.id}>
                      <td>#{a.attempt}</td>
                      <td>{a.timefinish ? new Date(a.timefinish * 1000).toLocaleString() : '—'}</td>
                      <td>
                        {Number.isFinite(pctAchieved) ? (
                          <strong>{pctAchieved.toFixed(0)}%</strong>
                        ) : (
                          '—'
                        )}
                      </td>
                      <td>
                        {hasPassThreshold ? (
                          <span
                            style={{
                              padding: '0.25rem 0.75rem',
                              borderRadius: 4,
                              fontWeight: 600,
                              fontSize: '0.8rem',
                              color: passed ? '#2e7d32' : '#b23c17',
                              backgroundColor: passed ? '#e8f5e9' : '#ffebee',
                              border: `1px solid ${passed ? '#2e7d32' : '#b23c17'}`,
                            }}
                          >
      {passed ? 'Passed' : 'Not Passed'}
    </span>
                        ) : (
                          <span
                            style={{
                              padding: '0.25rem 0.75rem',
                              borderRadius: 4,
                              fontWeight: 600,
                              fontSize: '0.8rem',
                              color: '#374151',
                              backgroundColor: '#f3f4f6',
                              border: '1px solid #d1d5db',
                            }}
                          >
      Completed
    </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
                </tbody>
              </table>
            </div>
          ) : (
            <p style={{ color: '#666', fontStyle: 'italic' }}>No finished attempts.</p>
          )}
        </div>
      </div>
    </div>
  );
}